<template>
  <div>
    <v-select
      v-if="isEditable"
      :options="statusOptions"
      v-model="status"
      :searchable="false"
      :clearable="false"
      :disabled="changeInProgress"
      :reduce="status => status.value"
      :placeholder="$t('status.selectSatus')"
      label="text">
    </v-select>

    <small
      v-if="!isEditable"
      class="text-danger text-right w-100">
      <strong>
        {{ availableStatuses[$i18n.locale][originalStatus] }}
      </strong>
    </small>

    <d-button
      v-if="originalStatus !== status && isEditable"
      class="ml-2 text-nowrap"
      @click.prevent="beforeChangeStatus">
      <template v-if="!changeInProgress">
        {{ $t('status.changeStatus') }}
      </template>
      <template v-if="changeInProgress">
        <div
          class="spinner-border spinner-border-sm"
          role="status"></div>
      </template>
    </d-button>
  </div>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';
import orderStatuses from './../../data/order-statuses.js';
import orderStatusPhase from './../../data/order-status-phase.js';
import Vue from 'vue';

export default {
  name: 'order-status',
  props: [
    'initialStatus',
    'isLocked',
    'settlementsNumber'
  ],
  computed: {
    isEditable () {
      if (this.originalStatus === 'DELETED' || this.originalStatus === 'TO_DELETE') {
        return false;
      }

      if (this.originalStatus === 'ENDED' && !this.userIsAdmin) {
        return false;
      }

      return true;
    },
    orderID () {
      return parseInt(this.$route.params.id, 10);
    },
    statusOptions () {
      let output = [];
      let isOffer = orderStatusPhase['offer'].slice(0, 3).indexOf(this.originalStatus) > -1;
      let keyValue = isOffer ? 'offer' : 'order';
      let keys = orderStatusPhase[keyValue];
      let currentStatusIndex = orderStatusPhase[keyValue].indexOf(this.originalStatus);

      if (!this.isLocked) {
        currentStatusIndex = 0;
      }

      for (let i = currentStatusIndex; i < keys.length; i++) {
        output.push({
          value: keys[i],
          text: orderStatuses[this.$i18n.locale][keys[i]]
        });
      }

      return output;
    },
    availableStatuses () {
      return orderStatuses;
    },
    userIsAdmin () {
      return this.$store.getters.getUserType === 'admin';
    }
  },
  data () {
    return {
      status: '',
      originalStatus: '',
      changeInProgress: false
    };
  },
  watch: {
    initialStatus (newValue) {
      this.originalStatus = newValue;
      this.status = this.originalStatus;
    }
  },
  mounted () {
    this.originalStatus = this.initialStatus;
    this.status = this.originalStatus;
    this.$bus.$on('order-creator-check-availability', this.receivedAvailability);
  },
  methods: {
    checkAvailability () {
      this.$bus.$emit('order-status-check-availability');
    },
    receivedAvailability (status) {
      if (status === 'ok') {
        this.changeStatus();
      } else if (status === 'no-availability-data') {
        Vue.swal({
          title: this.$t('order.orderContainsUnavailableProducts'),
          text: this.$t('order.someProductsLackAvailabilityInfoChangeAndTryAgain'),
          type: 'error',
          showCancelButton: false,
          confirmButtonText: this.$t('order.okIUnderstand')
        });
      } else if (status === 'no-stocks') {
        Vue.swal({
          title: this.$t('order.orderContainsUnavailableProducts'),
          text: this.$t('order.cannotPlaceOrderWithUnavailableProductsChangeAndTryAgain'),
          type: 'error',
          showCancelButton: false,
          confirmButtonText: this.$t('order.okIUnderstand')
        });
      } else if (status === 'has-container-products') {
        Vue.swal({
          title: this.$t('order.someProductsInDelivery'),
          text: this.$t('order.someProductsInDelivery') + this.$t('order.doYouReallyWantToChangeOrderSatus'),
          type: 'question',
          showCancelButton: true,
          confirmButtonText: this.$t('status.yesChangeStatus'),
          cancelButtonText: this.$t('ui.cancel')
        }).then((result) => {
          if (result.value) {
            this.changeStatus();
          }
        });
      }
    },
    beforeChangeStatus () {
      if (this.status === 'ORDER') {
        this.checkAvailability();
      } else {
        this.changeStatus();
      }
    },
    changeStatus () {
      this.changeInProgress = true;

      if (this.status === 'ENDED' && this.settlementsNumber === 0) {
        Vue.swal({
          title: this.$t('order.noSettlementsTitle'),
          text: this.$t('order.noSettlementsText'),
          type: 'question',
          showCancelButton: true,
          confirmButtonText: this.$t('status.yesChangeStatus'),
          cancelButtonText: this.$t('ui.cancel')
        }).then((result) => {
          if (result.value) {
            this.changeStatusConfirm();
          } else {
            this.changeInProgress = false;
          }
        });
      } else if (this.status === 'TO_DELETE' || this.status === 'DELETED') {
        Vue.swal({
          title: this.$t('status.changeStatusToDeleteConfirmTitle'),
          text: this.$t('status.changeStatusToDeleteConfirmText'),
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: this.$t('ui.yesChange'),
          cancelButtonText: this.$t('ui.cancel')
        }).then((result) => {
          if (result.value) {
            this.changeStatusConfirm();
          } else {
            this.changeInProgress = false;
          }
        });
      } else {
        this.changeStatusConfirm();
      }
    },
    changeStatusConfirm () {
      Vue.swal({
        title: this.$t('status.changeStatusConfirmTitle'),
        text: this.status === 'TO_DELETE' ? `${this.$t('order.reservationsInContainersAndSuppliersCancelledAfterStatusChange')} ${this.$t('status.changeStatusConfirmText')}` : this.$t('status.changeStatusConfirmText'),
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('ui.yesChange'),
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value) {
          FormUtils.submit({
            $http: this.$http,
            endpoint: '/orders/statuses/change-status/' + this.orderID,
            formData: {
              status: this.status
            },
            successTitle: this.$t('status.changeStatusSuccessTitle'),
            successText: this.$t('status.changeStatusSuccessText'),
            errorTitle: this.$t('ui.errorOccured'),
            errorText: this.$t('status.changeStatusErrorText'),
            successAction: () => {
              this.changeInProgress = false;
              this.originalStatus = this.status;
              this.$bus.$emit('new-order-status', this.status);
              this.$bus.$emit('order-wrapper-reload');
              this.$bus.$emit('sidebar-counters-refresh');
            },
            failAction: () => {
              this.changeInProgress = false;
            }
          });
        } else {
          this.status = this.originalStatus;
          this.changeInProgress = false;
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('order-creator-check-availability', this.receivedAvailability);
  }
};
</script>
